/**
 * TemplateBottomBar Component
 *
 * Bottom action bar for template editing with save and delete actions.
 * Only visible when editing a user template.
 */

import React from 'react';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

interface TemplateBottomBarProps {
  /** Whether save is in progress */
  isSaving: boolean;
  /** Whether there are unsaved changes */
  _hasChanges?: boolean;
  /** Callback to save template */
  onSave: () => void;
  /** Callback to delete template */
  onDelete: () => void;
}

export const TemplateBottomBar: React.FC<TemplateBottomBarProps> = ({
  isSaving,
  onSave,
  onDelete,
}) => {
  return (
    <Box
      component="footer"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 2,
        height: LAYOUT_DIMENSIONS.BOTTOM_BAR_HEIGHT,
        px: { xs: 2, sm: 3, md: 4 },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Delete Button */}
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={onDelete}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
        }}
      >
        Delete
      </Button>

      {/* Save Button */}
      <Button
        variant="contained"
        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        onClick={onSave}
        disabled={isSaving}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          minWidth: 120,
        }}
      >
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </Box>
  );
};
